/// <reference types="vite/client" />
import { useEffect, useState } from 'react';
import { queryOptions, useQuery, UseQueryResult } from '@tanstack/react-query';
import { useApplication } from 'merchant-api/unified';
import { Offer } from 'merchant-api/types';
import { offerOrchestrator } from '..';

export enum OfferProduct {
  TERM_LOAN = 'TermLoan',
  LINE_OF_CREDIT = 'LineOfCredit',
  DUAL_PRODUCT = 'DualProduct',
}

export interface UseOffersProps {
  isLoadingOffers: boolean;
  tlOffer?: Offer | null;
  locOffer?: Offer | null;
  isErrorOffers: any;
  selectedOffer?: Offer | null;
  product?: OfferProduct | null;
  isPartnerChannel?: boolean;
  query: UseQueryResult<any>;
}

interface OffersResponse {
  offers: Offer[];
}

const getOffers = (applicationId: string) =>
  offerOrchestrator.get<OffersResponse>('/v2/offers', {params: { applicationId }, validateStatus: status => [200, 404].includes(status)})
    .then((res) => res.data);

export const getOffersOpts = (applicationId: string) =>
  queryOptions<OffersResponse>({
    queryKey: ['offers', applicationId],
    queryFn: () => getOffers(applicationId),
    retry: 0,
  });

export function useOffers(): UseOffersProps {
  const { applicationData, isErrorApplication, isPartnerChannel } = useApplication(['APPROVED']);
  const query = useQuery({
    ...getOffersOpts(applicationData?.id!),
    enabled: !!applicationData?.id,
  });
  const { data, error } = query;
  const [locOffer, setLocOffer] = useState<Offer | null>();
  const [tlOffer, setTLOffer] = useState<Offer | null>();
  const [product, setProduct] = useState<OfferProduct | null>();

  const maxOffer = (offers): Offer | undefined =>
    offers?.length &&
    offers?.reduce((prev, curr) =>
      prev?.maxLineAmount > curr?.maxLineAmount ? prev : curr,
    );
  const filterProductOffer = (product: string, offers: any[]) =>
    offers?.filter((offer) => offer?.product === product && offer?.presentationStatus === 'ACTIVE');
  const selectedOffer = data?.offers?.filter(
    (offer) =>
      offer?.userActionStatus === 'SELECTED' &&
      offer?.presentationStatus === 'ACTIVE',
  );

  useEffect(() => {
    if (data?.offers) {
      const tlArray = filterProductOffer('TermLoan', data.offers);
      setTLOffer(tlArray?.length > 0 ? maxOffer(tlArray) : null);
      const locArray = filterProductOffer('LineOfCredit', data.offers);
      setLocOffer(locArray?.length > 0 ? maxOffer(locArray) : null);
    }
  }, [data]);

  useEffect(() => {
    locOffer !== undefined &&
      tlOffer !== undefined &&
      setProduct(
        locOffer && tlOffer
          ? OfferProduct.DUAL_PRODUCT
          : tlOffer
            ? OfferProduct.TERM_LOAN
            : locOffer
              ? OfferProduct.LINE_OF_CREDIT
              : null,
      );
  }, [locOffer, tlOffer]);

  return {
    tlOffer: tlOffer,
    locOffer: locOffer,
    isLoadingOffers: query.isLoading,
    isErrorOffers: error || isErrorApplication,
    selectedOffer: selectedOffer?.length ? maxOffer(selectedOffer) : null,
    product: product,
    isPartnerChannel: Boolean(isPartnerChannel),
    query,
  };
}

export default useOffers;
